<template>
	<div id="item-plan">
		<!-- Date Header -->
		<v-card flat color="lightgray">
			<v-card-text class="d-flex justify-space-between align-center">
				<div class="d-flex align-center primary--text">
					<v-icon class="me-2" color="primary">mdi-calendar</v-icon>
					<span class="text-h5 me-2 font-weight-bold">{{ moment().format('MMMM') }}</span>
					<span class="text-subtitle-2 font-weight-light">{{ moment().format('YYYY') }}</span>
				</div>
			</v-card-text>
		</v-card>

		<loader v-if="loading" />
		<v-container fluid v-else>
			<v-expansion-panels v-if="itemPlanMaterials.length">
				<v-expansion-panel
					v-for="specialtyItem in itemPlanMaterials"
					:key="specialtyItem.id"
				>
					<v-expansion-panel-header class="px-3">
						<div class="me-2 flex-grow-0">
							<v-icon>mdi-pill</v-icon>
						</div>
						<div>{{ specialtyItem.specialtyName }}</div>
					</v-expansion-panel-header>
					<v-expansion-panel-content class="px-3">
						<!-- Material Cards -->
						<v-row v-if="specialtyItem.materials.length" class="mb-3">
							<v-col
								v-for="material in specialtyItem.materials"
								:key="material.id"
								cols="12"
								class="py-2"
							>
								<v-card flat border>
									<v-card-text class="d-flex justify-space-between pa-4">
										<!-- Material Name and Form -->
										<div class="d-flex align-center mb-2">
											<span class="primary--text font-weight-bold text-body-1">
												{{ material.materialName || getMaterialById(material.materialId)?.name }}
											</span>
											<span class="ms-2 text-body-2">
												({{ getMaterialFormById(getMaterialById(material.materialId)?.materialFormId)?.name || '' }})
											</span>
										</div>
                                        
											<span class="ms-2">{{ $options.filters.formatNumber(material.amount, 3) }}</span>
                                        <span class="ms-2">{{ material.workloadName || getWorkloadById(material.workloadId)?.name }}</span>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>

						<!-- No Materials Message -->
						<div v-else class="text-center pa-4 grey--text text-body-2">
							{{ $t('ItemPlan.no-items-to-show') }}
						</div>
					</v-expansion-panel-content>
				</v-expansion-panel>
			</v-expansion-panels>

			<!-- Empty State -->
			<div
				v-else
				class="d-flex flex-column justify-center align-center"
				style="height: 45vh"
			>
				<v-icon x-large class="mb-3">mdi-pill</v-icon>
				<span class="d-flex flex-column justify-center align-center grey--text text-body-1">
					{{ $t('ItemPlan.no-items-to-show') }}
				</span>
			</div>
		</v-container>
	</div>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import Loader from '@/components/Loader.vue'

export default {
	name: 'ItemPlan',

	components: { Loader },

	data() {
		return {
			moment,
			loading: false,
		}
	},

	computed: {
		...mapState({
			specialties: state => state.specialties.specialties,
			rawItemPlanMaterials: state => state.materials.specialtyMaterials,
			workloads: state => state.workloads.workloads,
			materials: state => state.materials.materials,
			materialForms: state => state.materialForms.materialForms,
		}),

		...mapGetters({
			getSpecialtyById: 'specialties/getSpecialtyById',
			getWorkloadById: 'workloads/getWorkloadById',
			getMaterialById: 'materials/getMaterialById',
			getMaterialFormById: 'materialForms/getMaterialFormById'
		}),

		itemPlanMaterials() {
			if (!this.rawItemPlanMaterials || !this.rawItemPlanMaterials.length) {
				return []
			}

			const grouped = {}

			this.rawItemPlanMaterials.forEach(specialtyGroup => {
				const specialtyId = specialtyGroup.specialtyId
				if (!grouped[specialtyId]) {
					grouped[specialtyId] = {
						specialtyId,
						id: `specialty-${specialtyId}`,
						specialtyName: specialtyGroup.specialtyName || this.getSpecialtyById(specialtyId)?.name || 'Unknown',
						materials: []
					}
				}

				// Add workload materials if they exist
				if (specialtyGroup.workloads && specialtyGroup.workloads.length) {
					specialtyGroup.workloads.forEach(workload => {
						workload.materials.forEach(material => {
							grouped[specialtyId].materials.push({
								...material,
								workloadId: workload.workloadId,
								workloadName: workload.workloadName
							})
						})
					})
				}

				// Add direct materials
				if (specialtyGroup.materials && specialtyGroup.materials.length) {
					grouped[specialtyId].materials.push(...specialtyGroup.materials)
				}
			})

			return Object.values(grouped)
		}
	},

	created() {
		this.loading = true
		Promise.all([
			!this.specialties.length ? this.$store.dispatch('specialties/fetchAll') : null,
			!this.workloads.length ? this.$store.dispatch('workloads/fetchAll') : null,
			!this.materialForms.length ? this.$store.dispatch('materialForms/fetchAll') : null,
			this.$store.dispatch('materials/fetchAll', {}),
			!this.itemPlanMaterials.length
				? this.$store.dispatch('materials/fetchAllForSpecialtyVisits', {
					fromDate: moment().startOf('month').format('YYYY-MM-DD'),
					toDate: moment().endOf('month').format('YYYY-MM-DD'),
				})
				: null,
		]).finally(() => {
			this.loading = false
		})
	}
}
</script>

<style lang="scss" scoped>
#item-plan {
	.v-expansion-panel-content__wrap {
		padding: 0
	}

	.v-card {
		border: 1px solid #e0e0e0
	}
}
</style>